import { useEffect, useRef, useState } from "react";
import { Loader2, Search } from "lucide-react";
import api from "../../lib/api";
import { STATUS_LABELS } from "../../lib/product";
import { Input } from "../ui/input";

// A small search-as-you-type list: the caller says what a result is and what to do when one is chosen.
function SearchList({ request, render, onPick, placeholder, label, testid }) {
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const seq = useRef(0);

  useEffect(() => {
    const text = query.trim();
    if (text.length < 2) { setRows(null); return undefined; }
    const timer = setTimeout(async () => {
      const mine = ++seq.current;
      setLoading(true);
      try {
        const found = await request(text);
        if (mine === seq.current) setRows(found);
      } catch {
        if (mine === seq.current) setRows([]);
      } finally {
        if (mine === seq.current) setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={placeholder} className="pl-9" aria-label={label} data-testid={`${testid}-input`} />
        {loading && <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" aria-hidden="true" />}
      </div>
      {rows && rows.length === 0 && <p className="text-xs text-slate-500" data-testid={`${testid}-empty`}>Aucun résultat.</p>}
      {rows && rows.length > 0 && (
        <ul className="max-h-48 overflow-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid={`${testid}-results`}>
          {rows.map((row) => (
            <li key={row.id}>
              <button type="button" className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 focus:bg-slate-50 focus:outline-none" onClick={() => onPick(row)}
                data-testid={`${testid}-pick-${row.number}`}>
                {render(row)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Another product item (to merge into, or to depend on).
export function ItemPicker({ onPick, excludeIds = [], placeholder = "Numéro ou titre…", testid = "item-picker" }) {
  const request = async (text) => {
    const { data } = await api.get("/product/items", { params: { search: text, limit: 8 } });
    return data.items.filter((i) => !excludeIds.includes(i.id));
  };
  return (
    <SearchList request={request} onPick={onPick} placeholder={placeholder} label="Rechercher un retour produit" testid={testid}
      render={(i) => (
        <span className="block">
          <span className="block text-xs text-slate-500 tabular-nums">{i.number} · {STATUS_LABELS[i.status]}</span>
          <span className="block text-slate-900 break-words">{i.title}</span>
        </span>
      )} />
  );
}

// A support ticket the person already sees (the server answers only with those).
export function TicketPicker({ onPick, excludeIds = [], testid = "ticket-picker" }) {
  const request = async (text) => {
    const { data } = await api.get("/tickets", { params: { search: text, limit: 8 } });
    return data.items.filter((t) => !excludeIds.includes(t.id));
  };
  return (
    <SearchList request={request} onPick={onPick} placeholder="Numéro ou titre du ticket…" label="Rechercher un ticket" testid={testid}
      render={(t) => (
        <span className="block">
          <span className="block text-xs text-slate-500 tabular-nums">{t.number}</span>
          <span className="block text-slate-900 break-words">{t.title}</span>
        </span>
      )} />
  );
}
